import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../lib/AuthContext";
import { useSettings } from "../lib/SettingsContext";
import { api, formatPrice } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Upload, X, Edit3, Image as ImageIcon, Loader2, GripVertical, Truck, Phone, Copy, ExternalLink } from "lucide-react";

const TABS = ["Tablou de Bord", "Produse", "Adaugă / Editează", "Import CSV", "Comenzi", "Utilizatori", "Mesaje", "Conținut Site"];
const CATEGORIES = ["Mobilier Antic","Ceasuri Vintage","Picturi Rare","Bijuterii de Lux","Colecții Rare","Porțelan","Decorațiuni Vintage","Sculpturi"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const EMPTY = { name: "", category: "Mobilier Antic", price: "", description: "", history: "", origin: "", materials: "", dimensions: "", condition: "Excelentă", images: [], stock: 1, featured: false, rare: false, trending: false };

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null); // product object or null

  const loadAll = async () => {
    try {
      const [a, p, o, u, m] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/products", { params: { limit: 500 } }),
        api.get("/admin/orders"),
        api.get("/admin/users"),
        api.get("/admin/messages"),
      ]);
      setAnalytics(a.data); setProducts(p.data); setOrders(o.data); setUsers(u.data); setMessages(m.data);
    } catch (e) { /* */ }
  };

  useEffect(() => { if (user?.is_admin) loadAll(); }, [user]);

  const startNew = () => { setEditing({ ...EMPTY }); setTab(2); };
  const startEdit = (p) => { setEditing({ ...p, price: p.price.toString() }); setTab(2); };

  const deleteProduct = async (id) => {
    if (!window.confirm("Ștergi produsul?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Șters"); loadAll();
  };

  const updateOrder = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { tracking_status: status });
    toast.success("Status actualizat"); loadAll();
  };

  if (loading) return <main className="pt-40 text-center text-[#A39E8A]">Se încarcă...</main>;
  if (!user || !user.is_admin) return <main className="pt-40 text-center text-[#A39E8A]" data-testid="admin-denied">Acces interzis. Doar administratorii pot accesa această pagină.</main>;

  return (
    <main className="pt-32 pb-20 min-h-screen" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <header className="mb-12 flex justify-between items-end flex-wrap gap-4">
          <div>
            <p className="label-luxe text-[#D4AF37] mb-3">Panou Administrator</p>
            <h1 className="h-display text-5xl text-[#F8F4E6]">Johny's Antique Shop · Control</h1>
          </div>
          <button onClick={startNew} className="btn-gold" data-testid="header-add-product"><Plus size={14} /> Adaugă Produs</button>
        </header>
        <nav className="flex gap-2 sm:gap-6 border-b border-[#D4AF37]/15 mb-12 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t, i) => (
            <button key={t} onClick={() => { setTab(i); if (i !== 2) setEditing(null); }} className={`pb-4 px-2 text-[11px] uppercase tracking-[0.25em] whitespace-nowrap transition ${tab === i ? "text-[#D4AF37] border-b border-[#D4AF37]" : "text-[#A39E8A] hover:text-[#F8F4E6]"}`} data-testid={`admin-tab-${i}`}>{t}</button>
          ))}
        </nav>

        {tab === 0 && analytics && (
          <section className="space-y-12" data-testid="admin-dashboard">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[["Comenzi", analytics.total_orders],["Utilizatori", analytics.total_users],["Produse", analytics.total_products],["Venituri", formatPrice(analytics.revenue)]].map(([l, v]) => (
                <div key={l} className="border border-[#D4AF37]/15 p-8" data-testid={`stat-${l}`}>
                  <p className="label-luxe">{l}</p>
                  <p className="font-serif text-4xl text-[#D4AF37] mt-3">{v}</p>
                </div>
              ))}
            </div>
            <div>
              <h3 className="font-serif text-2xl text-[#F8F4E6] mb-4">Cele Mai Recenzate Piese</h3>
              <ul className="space-y-3">
                {analytics.top_products.map((p, i) => (
                  <li key={i} className="flex justify-between border-b border-[#D4AF37]/10 py-3 text-sm">
                    <span className="text-[#F8F4E6]">{p.name}</span>
                    <span className="text-[#D4AF37]">★ {(p.rating || 0).toFixed(1)} ({p.reviews_count || 0})</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {tab === 1 && (
          <section data-testid="admin-products">
            <h2 className="font-serif text-2xl text-[#F8F4E6] mb-6">{products.length} Produse</h2>
            <ul className="divide-y divide-[#D4AF37]/10">
              {products.map(p => (
                <li key={p.id} className="py-4 flex items-center gap-4" data-testid={`prod-row-${p.id}`}>
                  <img src={fullUrl(p.images?.[0])} alt="" className="w-16 h-16 object-cover bg-[#0F0F0F]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#F8F4E6] truncate">{p.name}</p>
                    <p className="text-xs text-[#A39E8A]">{p.category} · Stoc: {p.stock}</p>
                  </div>
                  <span className="font-serif text-[#D4AF37]">{formatPrice(p.price)}</span>
                  <button onClick={() => startEdit(p)} className="text-[#A39E8A] hover:text-[#D4AF37]" title="Editează" data-testid={`edit-${p.id}`}><Edit3 size={16} /></button>
                  <button onClick={() => deleteProduct(p.id)} className="text-[#A39E8A] hover:text-red-400" title="Șterge" data-testid={`del-prod-${p.id}`}><Trash2 size={16} /></button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {tab === 2 && <ProductEditor product={editing || EMPTY} onDone={() => { loadAll(); setEditing(null); setTab(1); }} onCancel={() => { setEditing(null); setTab(1); }} />}

        {tab === 3 && <BulkImport reload={loadAll} />}

        {tab === 4 && (
          <section data-testid="admin-orders">
            <ul className="space-y-3">
              {orders.length === 0 ? <p className="text-[#A39E8A]">Nicio comandă.</p> : orders.map(o => (
                <OrderAdminRow key={o.id} order={o} onUpdate={loadAll} />
              ))}
            </ul>
          </section>
        )}

        {tab === 5 && (
          <section data-testid="admin-users">
            <p className="text-sm text-[#A39E8A] mb-6">Click pe „Promovează admin” pentru a-i da unui utilizator drepturi de administrator. Administratorul principal (setat în configurare) nu poate fi modificat.</p>
            <ul className="divide-y divide-[#D4AF37]/10">
              {users.map(u => (
                <li key={u.user_id} className="py-4 flex justify-between items-center gap-4 flex-wrap" data-testid={`user-row-${u.user_id}`}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {u.picture && <img src={u.picture} alt="" className="w-10 h-10 rounded-full border border-[#D4AF37]/30 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-[#F8F4E6] truncate">{u.name}</p>
                      <p className="text-xs text-[#A39E8A] truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {u.is_primary_admin ? (
                      <span className="label-luxe text-[#D4AF37]" title="Setat în configurare">Admin Principal</span>
                    ) : u.user_id === user.user_id ? (
                      <span className="label-luxe text-[#A39E8A]">Tu</span>
                    ) : u.is_admin ? (
                      <>
                        <span className="label-luxe text-[#D4AF37]">Admin</span>
                        <button onClick={async () => { await api.put(`/admin/users/${u.user_id}/admin`, { is_admin: false }); toast.success("Drepturi retrase"); loadAll(); }} className="text-[10px] uppercase tracking-[0.2em] text-[#A39E8A] hover:text-red-400 border-b border-[#A39E8A]/30 pb-0.5" data-testid={`demote-${u.user_id}`}>Retrage Admin</button>
                      </>
                    ) : (
                      <button onClick={async () => { await api.put(`/admin/users/${u.user_id}/admin`, { is_admin: true }); toast.success("Promovat la Admin"); loadAll(); }} className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] border-b border-[#D4AF37]/40 pb-0.5 hover:border-[#D4AF37]" data-testid={`promote-${u.user_id}`}>Promovează Admin</button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {tab === 6 && (
          <section data-testid="admin-messages">
            <ul className="space-y-3">
              {messages.length === 0 ? <p className="text-[#A39E8A]">Niciun mesaj.</p> : messages.map(m => (
                <li key={m.id} className="border border-[#D4AF37]/15 p-4">
                  <p className="label-luxe text-[#D4AF37]">{m.user_name} · {m.from_admin ? "Admin" : "Client"}</p>
                  <p className="text-[#F8F4E6] mt-2">{m.text}</p>
                  <p className="text-xs text-[#A39E8A] mt-2">{new Date(m.created_at).toLocaleString("ro-RO")}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {tab === 7 && <SiteContentEditor />}
      </div>
    </main>
  );
}


function ProductEditor({ product, onDone, onCancel }) {
  const [form, setForm] = useState(product);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { setForm(product); }, [product]);

  const isEdit = !!product.id;

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      if (!file.type.startsWith("image/")) { toast.error(`${file.name}: nu este imagine`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(r.data.url);
      } catch (e) { toast.error(`Eroare upload: ${file.name}`); }
    }
    if (newUrls.length) {
      setForm(f => ({ ...f, images: [...(f.images || []), ...newUrls] }));
      toast.success(`${newUrls.length} imagine${newUrls.length > 1 ? 'i încărcate' : ' încărcată'}`);
    }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    upload(Array.from(e.dataTransfer.files));
  };

  const removeImage = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const moveImage = (idx, dir) => {
    setForm(f => {
      const imgs = [...f.images];
      const ni = idx + dir;
      if (ni < 0 || ni >= imgs.length) return f;
      [imgs[idx], imgs[ni]] = [imgs[ni], imgs[idx]];
      return { ...f, images: imgs };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 1 };
    try {
      if (isEdit) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Actualizat");
      } else {
        await api.post("/products", payload);
        toast.success("Produs adăugat");
      }
      onDone();
    } catch (e) { toast.error("Eroare la salvare"); }
    setSaving(false);
  };

  return (
    <section data-testid="product-editor">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <h2 className="font-serif text-3xl text-[#F8F4E6]">{isEdit ? "Editează Produs" : "Produs Nou"}</h2>
        <button onClick={onCancel} className="text-[#A39E8A] hover:text-[#D4AF37] flex items-center gap-2 text-sm" data-testid="cancel-edit"><X size={16} /> Anulează</button>
      </div>
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_320px] gap-12">
        <div className="space-y-8">
          {/* IMAGES */}
          <div>
            <p className="label-luxe text-[#D4AF37] mb-4">Imagini ({form.images?.length || 0})</p>
            <div
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed p-10 text-center cursor-pointer transition ${dragActive ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/25 hover:border-[#D4AF37]/50"}`}
              data-testid="image-dropzone"
            >
              <Upload size={28} strokeWidth={1.2} className="text-[#D4AF37] mx-auto mb-3" />
              <p className="text-[#F8F4E6] font-serif text-lg mb-1">{uploading ? "Se încarcă..." : "Trage imagini aici sau click pentru a alege"}</p>
              <p className="text-xs text-[#A39E8A]">JPEG, PNG, WebP, GIF · max 10MB / imagine · poți selecta mai multe</p>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => upload(Array.from(e.target.files))} data-testid="image-input" />
            </div>

            {form.images?.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                {form.images.map((url, i) => (
                  <div key={i} className="relative group aspect-square bg-[#0F0F0F] border border-[#D4AF37]/15" data-testid={`img-tile-${i}`}>
                    <img src={fullUrl(url)} alt="" className="w-full h-full object-cover" />
                    {i === 0 && <span className="absolute top-2 left-2 bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest px-2 py-1">Principală</span>}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                      {i > 0 && <button type="button" onClick={() => moveImage(i, -1)} className="bg-[#D4AF37] text-black p-1.5 text-[10px] uppercase tracking-widest" title="Mută la stânga">←</button>}
                      {i < form.images.length - 1 && <button type="button" onClick={() => moveImage(i, 1)} className="bg-[#D4AF37] text-black p-1.5 text-[10px] uppercase tracking-widest" title="Mută la dreapta">→</button>}
                      <button type="button" onClick={() => removeImage(i)} className="bg-red-500 text-white p-1.5" title="Șterge" data-testid={`remove-img-${i}`}><X size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* INFO */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className="label-luxe block mb-2">Nume produs *</label>
              <input className="input-luxe" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="ed-name" />
            </div>
            <div>
              <label className="label-luxe block mb-2">Categorie *</label>
              <select className="input-luxe" value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="ed-category">
                {CATEGORIES.map(c => <option key={c} value={c} className="bg-[#0F0F0F]">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-luxe block mb-2">Preț (RON) *</label>
              <input className="input-luxe" type="number" step="0.01" required value={form.price} onChange={e => setForm({...form, price: e.target.value})} data-testid="ed-price" />
            </div>
            <div>
              <label className="label-luxe block mb-2">Stoc</label>
              <input className="input-luxe" type="number" min="0" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} data-testid="ed-stock" />
            </div>
            <div>
              <label className="label-luxe block mb-2">Stare</label>
              <input className="input-luxe" value={form.condition} onChange={e => setForm({...form, condition: e.target.value})} data-testid="ed-condition" />
            </div>
            <div>
              <label className="label-luxe block mb-2">Origine</label>
              <input className="input-luxe" value={form.origin} onChange={e => setForm({...form, origin: e.target.value})} data-testid="ed-origin" />
            </div>
            <div>
              <label className="label-luxe block mb-2">Materiale</label>
              <input className="input-luxe" value={form.materials} onChange={e => setForm({...form, materials: e.target.value})} data-testid="ed-materials" />
            </div>
            <div>
              <label className="label-luxe block mb-2">Dimensiuni</label>
              <input className="input-luxe" value={form.dimensions} onChange={e => setForm({...form, dimensions: e.target.value})} data-testid="ed-dimensions" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-luxe block mb-2">Descriere *</label>
              <textarea className="input-luxe" required value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="ed-description" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-luxe block mb-2">Istorie & proveniență</label>
              <textarea className="input-luxe" value={form.history} onChange={e => setForm({...form, history: e.target.value})} data-testid="ed-history" />
            </div>
          </div>
        </div>

        {/* SIDEBAR */}
        <aside className="bg-[#0a0a0a] border border-[#D4AF37]/15 p-6 h-fit lg:sticky lg:top-28 space-y-6">
          <div>
            <p className="label-luxe text-[#D4AF37] mb-4">Vizibilitate</p>
            <div className="space-y-3">
              <label className="flex items-center gap-3 text-sm text-[#F8F4E6] cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} className="accent-[#D4AF37] w-4 h-4" data-testid="ed-featured" />
                <span>Produs reprezentativ <span className="text-[#A39E8A] text-xs block">apare pe pagina principală</span></span>
              </label>
              <label className="flex items-center gap-3 text-sm text-[#F8F4E6] cursor-pointer">
                <input type="checkbox" checked={form.rare} onChange={e => setForm({...form, rare: e.target.checked})} className="accent-[#D4AF37] w-4 h-4" data-testid="ed-rare" />
                <span>Piesă rară <span className="text-[#A39E8A] text-xs block">badge „Rar” pe card</span></span>
              </label>
              <label className="flex items-center gap-3 text-sm text-[#F8F4E6] cursor-pointer">
                <input type="checkbox" checked={form.trending} onChange={e => setForm({...form, trending: e.target.checked})} className="accent-[#D4AF37] w-4 h-4" data-testid="ed-trending" />
                <span>În tendințe</span>
              </label>
            </div>
          </div>

          <div className="border-t border-[#D4AF37]/10 pt-6">
            <button type="submit" disabled={saving || uploading} className="btn-gold w-full justify-center" data-testid="save-product">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {saving ? "Se salvează..." : isEdit ? "Salvează Modificări" : "Publică Produs"}
            </button>
            <button type="button" onClick={onCancel} className="w-full text-center text-xs uppercase tracking-[0.25em] text-[#A39E8A] hover:text-[#D4AF37] mt-4">Anulează</button>
          </div>
        </aside>
      </form>
    </section>
  );
}


function OrderAdminRow({ order, onUpdate }) {
  const [awb, setAwb] = useState(order.awb || "");
  const [courierId, setCourierId] = useState(order.courier_id || "sameday");
  const [savingAwb, setSavingAwb] = useState(false);
  const [callCourierOpen, setCallCourierOpen] = useState(false);
  const COURIERS = [
    { id: "sameday", name: "Sameday / Easybox" },
    { id: "fan", name: "FAN Courier" },
    { id: "dpd", name: "DPD" },
    { id: "gls", name: "GLS" },
    { id: "cargus", name: "Cargus" },
  ];

  const saveAwb = async () => {
    setSavingAwb(true);
    try {
      await api.put(`/admin/orders/${order.id}/status`, { awb, courier_id: courierId });
      toast.success("AWB salvat");
      onUpdate();
    } catch { toast.error("Eroare la salvare AWB"); }
    setSavingAwb(false);
  };

  const updateStatus = async (status) => {
    try {
      const r = await api.put(`/admin/orders/${order.id}/status`, { tracking_status: status });
      if (status === "Expediată" && !awb.trim()) {
        toast.warning("Comandă marcată Expediată — clientul NU primește email fără AWB completat");
      } else if (status === "Expediată") {
        toast.success("Email cu AWB trimis clientului");
      } else {
        toast.success("Status actualizat");
      }
      onUpdate();
    } catch { toast.error("Eroare la actualizare"); }
  };

  return (
    <li className="border border-[#D4AF37]/15 p-5 space-y-4" data-testid={`adm-order-${order.id}`}>
      <div className="flex flex-wrap gap-4 items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="label-luxe text-[#D4AF37]">#{order.id}</p>
          <p className="text-[#F8F4E6] mt-1">{order.items_summary}</p>
          <p className="text-xs text-[#A39E8A] mt-1">{order.email} · {new Date(order.created_at).toLocaleString("ro-RO")} · {order.payment_method === "bank_transfer" ? "Transfer bancar" : "Card"}</p>
          {order.shipping_address && (
            <p className="text-xs text-[#A39E8A] mt-2 leading-relaxed">
              📍 {order.shipping_address.full_name}, {order.shipping_address.phone} — {order.shipping_address.address}, {order.shipping_address.city}, {order.shipping_address.county} {order.shipping_address.postal_code}
            </p>
          )}
        </div>
        <div className="flex gap-3 items-center flex-shrink-0">
          <span className="font-serif text-xl text-[#D4AF37]">{formatPrice(order.amount, order.currency?.toUpperCase())}</span>
          <select onChange={e => updateStatus(e.target.value)} value={order.tracking_status || "Procesare"} className="bg-transparent border border-[#D4AF37]/30 px-3 py-1 text-xs text-[#F8F4E6]" data-testid={`status-${order.id}`}>
            {["Așteptare plată","Procesare","Pregătită","Expediată","Livrată"].map(s => <option key={s} value={s} className="bg-[#0F0F0F]">{s}</option>)}
          </select>
        </div>
      </div>

      {/* AWB Section */}
      <div className="border-t border-[#D4AF37]/10 pt-4 flex flex-wrap gap-3 items-center">
        <p className="label-luxe text-[#A39E8A] flex-shrink-0">AWB & Curier:</p>
        <select value={courierId} onChange={e => setCourierId(e.target.value)} className="bg-[#0F0F0F] border border-[#D4AF37]/25 px-3 py-2 text-xs text-[#F8F4E6]" data-testid={`courier-select-${order.id}`}>
          {COURIERS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input
          value={awb}
          onChange={e => setAwb(e.target.value)}
          placeholder="Introdu număr AWB (ex: 2110A0123456)"
          className="flex-1 min-w-[200px] bg-[#0F0F0F] border border-[#D4AF37]/25 px-3 py-2 text-sm text-[#F8F4E6] outline-none focus:border-[#D4AF37]"
          data-testid={`awb-input-${order.id}`}
        />
        <button onClick={saveAwb} disabled={savingAwb} className="btn-gold !py-2 !px-5 !text-[10px]" data-testid={`save-awb-${order.id}`}>
          {savingAwb ? "..." : "Salvează AWB"}
        </button>
        {order.tracking_url && (
          <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] border-b border-[#D4AF37]/40 pb-0.5" data-testid={`track-link-${order.id}`}>
            Urmărire ↗
          </a>
        )}
      </div>

      {/* Cheamă Curier - shortcut */}
      <div className="pt-2">
        <button onClick={() => setCallCourierOpen(true)} className="btn-ghost !py-2 !px-4 !text-[10px] flex items-center gap-2" data-testid={`call-courier-${order.id}`}>
          <Truck size={14} /> Cheamă Curier pentru Ridicare
        </button>
      </div>

      {callCourierOpen && <CallCourierModal order={order} onClose={() => setCallCourierOpen(false)} />}
    </li>
  );
}


function CallCourierModal({ order, onClose }) {
  const { settings } = useSettings();
  const senderName = settings.contact_business_name || "Johny's Antiques Shop";
  const senderPhone = settings.contact_phone || "";
  const senderAddress = settings.contact_address || "";
  const addr = order.shipping_address || {};

  const summary = `EXPEDIERE — ${order.id}\n\n` +
    `EXPEDITOR:\n${senderName}\n${senderAddress}\nTel: ${senderPhone}\n\n` +
    `DESTINATAR:\n${addr.full_name || ""}\nTel: ${addr.phone || ""}\n${addr.address || ""}\n${addr.city || ""}, ${addr.county || ""} ${addr.postal_code || ""}\n\n` +
    `CONȚINUT: ${order.items_summary || ""}\n` +
    `VALOARE DECLARATĂ: ${order.subtotal || order.amount} RON`;

  const copyAll = () => { navigator.clipboard.writeText(summary); toast.success("Detalii copiate în clipboard"); };
  const copyAddress = () => {
    const t = `${addr.full_name}, ${addr.phone}, ${addr.address}, ${addr.city}, ${addr.county} ${addr.postal_code || ""}`.trim();
    navigator.clipboard.writeText(t); toast.success("Adresa destinatarului copiată");
  };

  const COURIER_PICKUP = [
    { id: "sameday", name: "Sameday / Easybox", phone: "021 9192",
      url: "https://sameday.ro/trimite/",
      note: "Trimit acum — completezi online, plătești cu cardul, curierul vine să ridice" },
    { id: "cargus", name: "Cargus Trimit Acum", phone: "021 9330",
      url: "https://urgentcargus.ro/trimiteri-rapide/",
      note: "Trimit acum — expediere fără contract, plată online" },
    { id: "fan", name: "FAN Courier", phone: "021 9336",
      url: "https://www.fancourier.ro/",
      note: "Sună pentru pickup sau intră online să programezi ridicarea" },
    { id: "dpd", name: "DPD Romania", phone: "021 222 1777",
      url: "https://www.dpd.com/ro/ro/expedieri/",
      note: "Pickup la adresă sau lasă la un DPD Pickup Point" },
    { id: "gls", name: "GLS Romania", phone: "031 620 9060",
      url: "https://gls-group.eu/RO/ro/servicii/",
      note: "Pickup la adresă cu programare online" },
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="courier-modal">
      <div onClick={e => e.stopPropagation()} className="bg-[#0a0a0a] border border-[#D4AF37]/25 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#D4AF37]/15 flex justify-between items-center">
          <div>
            <p className="label-luxe text-[#D4AF37]">Ridicare Colet — Comanda #{order.id}</p>
            <h3 className="font-serif text-2xl text-[#F8F4E6] mt-1">Cheamă un Curier</h3>
          </div>
          <button onClick={onClose} className="text-[#A39E8A] hover:text-[#D4AF37]" data-testid="close-modal"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Detalii expediere */}
          <div className="border border-[#D4AF37]/15 p-5 bg-[#0F0F0F]">
            <div className="flex justify-between items-start mb-3">
              <p className="label-luxe text-[#D4AF37]">Detalii Expediere</p>
              <div className="flex gap-2">
                <button onClick={copyAddress} className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] border-b border-[#D4AF37]/40 pb-0.5 flex items-center gap-1" data-testid="copy-addr"><Copy size={10} /> Adresă</button>
                <button onClick={copyAll} className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] border-b border-[#D4AF37]/40 pb-0.5 flex items-center gap-1" data-testid="copy-all"><Copy size={10} /> Totul</button>
              </div>
            </div>
            <pre className="text-xs text-[#F8F4E6] font-mono whitespace-pre-wrap leading-relaxed">{summary}</pre>
          </div>

          <p className="text-sm text-[#A39E8A]">
            Alege curierul dorit. Click pe „Deschide Site" pentru a programa ridicarea online, sau sună la numărul afișat pentru pickup rapid.
          </p>

          <div className="space-y-3">
            {COURIER_PICKUP.map(c => (
              <div key={c.id} className="border border-[#D4AF37]/15 p-4 hover:border-[#D4AF37]/40 transition" data-testid={`courier-option-${c.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg text-[#F8F4E6]">{c.name}</p>
                    <p className="text-xs text-[#A39E8A] mt-1">{c.note}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <a href={`tel:${c.phone.replace(/\s/g,"")}`} className="btn-ghost !py-2 !px-4 !text-[10px] flex items-center gap-2" data-testid={`call-${c.id}`}>
                      <Phone size={12} /> {c.phone}
                    </a>
                    <a href={c.url} target="_blank" rel="noreferrer" className="btn-gold !py-2 !px-4 !text-[10px] flex items-center gap-2" data-testid={`open-${c.id}`}>
                      <ExternalLink size={12} /> Deschide Site
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#D4AF37]/10 pt-5 text-xs text-[#A39E8A] leading-relaxed">
            <p className="text-[#F8F4E6] mb-2">💡 Sfat rapid:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Click pe <strong>„Totul"</strong> pentru a copia toate detaliile în clipboard</li>
              <li>Deschide site-ul curierului preferat cu butonul auriu</li>
              <li>Lipești (Ctrl+V) detaliile în formularul de expediere</li>
              <li>După ce primești AWB-ul, revii aici și-l introduci în comandă</li>
              <li>Schimbi statusul pe „Expediată" → clientul primește email automat</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}


function BulkImport({ reload }) {
  const [csvText, setCsvText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const SAMPLE = `name,category,price,stock,description,origin,materials,dimensions,condition,images,featured,rare,trending
Vaza Ming sec XVII,Porțelan,890,1,"Vază chinezească porțelan albastru-alb dinastia Ming",China,Porțelan,Î 28cm,Excelentă,"https://example.com/img1.jpg,https://example.com/img2.jpg",true,true,false
Sabie ofițerească 1916,Artefacte Istorice,650,1,"Sabie ofițer român Primul Război Mondial",România,Oțel damasc lemn,L 95cm,Foarte bună,https://example.com/sabie.jpg,false,true,false`;

  const parseLine = (line) => {
    const result = []; let current = ""; let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' && line[i+1] === '"') { current += '"'; i++; }
      else if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === "," && !inQuotes) { result.push(current); current = ""; }
      else { current += ch; }
    }
    result.push(current);
    return result.map(s => s.trim());
  };

  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const headers = parseLine(lines[0]);
    return lines.slice(1).map(line => {
      const fields = parseLine(line); const obj = {};
      headers.forEach((h, i) => { obj[h.trim()] = fields[i] !== undefined ? fields[i] : ""; });
      return obj;
    });
  };

  const handleImport = async () => {
    setBusy(true); setResult(null);
    try {
      const rows = parseCSV(csvText);
      const products = rows.map(r => ({
        ...r,
        price: parseFloat(r.price) || 0,
        stock: parseInt(r.stock) || 1,
        featured: ["true","1","yes","da"].includes(String(r.featured||"").toLowerCase()),
        rare: ["true","1","yes","da"].includes(String(r.rare||"").toLowerCase()),
        trending: ["true","1","yes","da"].includes(String(r.trending||"").toLowerCase()),
        images: (r.images||"").split(",").map(s => s.trim()).filter(Boolean),
      }));
      const r = await api.post("/products/bulk-import", { products });
      setResult(r.data);
      toast.success(`Importate ${r.data.inserted} produse`);
      reload();
    } catch (e) { toast.error("Eroare la import"); }
    setBusy(false);
  };

  return (
    <section data-testid="bulk-import">
      <h2 className="font-serif text-2xl text-[#F8F4E6] mb-4">Import Bulk Produse (CSV)</h2>
      <div className="border border-[#D4AF37]/15 p-6 mb-6">
        <p className="text-sm text-[#A39E8A] mb-4 leading-relaxed">
          Lipește mai jos un CSV cu produsele tale. Coloane: <span className="text-[#D4AF37]">name, category, price, stock, description, history, origin, materials, dimensions, condition, images, featured, rare, trending</span>.
          Imaginile pot fi mai multe URL-uri separate prin virgulă (în interiorul ghilimelelor). featured/rare/trending acceptă <em>true/false</em>.
        </p>
        <button onClick={() => setCsvText(SAMPLE)} className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] border-b border-[#D4AF37]/40 mb-4" data-testid="load-sample">Încarcă Exemplu</button>
        <textarea value={csvText} onChange={e => setCsvText(e.target.value)} placeholder="Lipește aici CSV-ul..." className="w-full bg-[#0F0F0F] border border-[#D4AF37]/20 p-4 text-[#F8F4E6] font-mono text-xs min-h-[300px] outline-none focus:border-[#D4AF37]" data-testid="csv-input" />
        <button onClick={handleImport} disabled={!csvText.trim() || busy} className="btn-gold mt-4" data-testid="import-csv">
          {busy ? "Import în curs..." : "Importă în Magazin"}
        </button>
      </div>
      {result && (
        <div className="border border-[#D4AF37]/15 p-6" data-testid="import-result">
          <p className="font-serif text-xl text-[#D4AF37] mb-2">Rezultat: {result.inserted} / {result.total} importate</p>
          {result.errors?.length > 0 && (
            <ul className="text-sm text-[#A39E8A] space-y-1 mt-2">
              {result.errors.map((e, i) => <li key={i}>Rând {e.row}: {e.error}</li>)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}


const SECTIONS = [
  { key: "brand", title: "Brand & Identitate", fields: [
    { name: "brand_name", label: "Nume Brand (Navbar + Footer)", placeholder: "Johny's Antique Shop" },
    { name: "brand_logo", label: "Logo (URL imagine)", placeholder: "https://...", mono: true },
    { name: "brand_tagline", label: "Tagline mic deasupra titlului Hero", placeholder: "Casa de Antichități · Estd. 1987" },
    { name: "footer_description", label: "Descriere Footer", textarea: true, placeholder: "Casă de antichități rafinată..." },
  ]},
  { key: "hero", title: "Pagina Principală (Hero)", fields: [
    { name: "hero_title_main", label: "Titlu mare (linia 1)", placeholder: "Moștenirea" },
    { name: "hero_title_accent", label: "Titlu auriu (linia 2)", placeholder: "Timpului" },
    { name: "hero_subtitle", label: "Subtitlu", textarea: true, placeholder: "Descoperă obiecte de artă..." },
    { name: "hero_cta_primary", label: "Buton principal", placeholder: "Descoperă Colecția" },
    { name: "hero_cta_secondary", label: "Buton secundar", placeholder: "Povestea Casei" },
  ]},
  { key: "newsletter", title: "Newsletter (Footer)", fields: [
    { name: "newsletter_title", label: "Titlu Newsletter", placeholder: "Jurnalul Nostru" },
    { name: "newsletter_subtitle", label: "Descriere Newsletter", textarea: true, placeholder: "Înscrie-te pentru..." },
  ]},
  { key: "contact", title: "Pagina Contact", fields: [
    { name: "contact_business_name", label: "Nume Galerie/Firmă", placeholder: "Johny's Antique Shop" },
    { name: "contact_address", label: "Adresa completă (folosită și pentru Google Maps)", textarea: true, placeholder: "Șoseaua..." },
    { name: "contact_phone", label: "Telefon principal (cu prefix)", placeholder: "+40 755 680 215" },
    { name: "contact_phone_alt", label: "Telefon alternativ (opțional)", placeholder: "0755 680 215" },
    { name: "contact_email", label: "Email contact", placeholder: "contact@example.ro" },
    { name: "whatsapp_phone", label: "Număr WhatsApp (buton verde jos-dreapta)", placeholder: "+40755680215" },
  ]},
  { key: "social", title: "Rețele Sociale", fields: [
    { name: "social_tiktok", label: "Link TikTok (lasă gol pentru a ascunde)", placeholder: "https://www.tiktok.com/@..." },
    { name: "social_instagram", label: "Link Instagram (lasă gol pentru a ascunde)", placeholder: "https://www.instagram.com/..." },
    { name: "social_facebook", label: "Link Facebook (lasă gol pentru a ascunde)", placeholder: "https://www.facebook.com/..." },
  ]},
  { key: "bank", title: "Date Bancare (pentru Transfer Bancar la checkout)", fields: [
    { name: "bank_iban", label: "IBAN", placeholder: "RO__ ____ ____ ____ ____ ____", mono: true },
    { name: "bank_holder", label: "Titular cont", placeholder: "Numele complet sau firma" },
    { name: "bank_name", label: "Banca (opțional)", placeholder: "BCR / BT / ING / Revolut" },
  ]},
  { key: "about", title: "Pagina Despre Noi", fields: [
    { name: "about_short", label: "Text scurt (sub titlu)", textarea: true, placeholder: "Fondată în 1987..." },
    { name: "about_long", label: "Text lung (secțiunea Filosofie)", textarea: true, placeholder: "Specializați în..." },
    { name: "about_stat_years", label: "Statistică 1 - Ani de Experiență", placeholder: "38+" },
    { name: "about_stat_pieces", label: "Statistică 2 - Piese Vândute", placeholder: "1200+" },
    { name: "about_stat_countries", label: "Statistică 3 - Țări Reprezentate", placeholder: "12" },
  ]},
];


function SiteContentEditor() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState("brand");

  useEffect(() => { api.get("/site-settings").then(r => setForm(r.data)).catch(() => {}); }, []);

  if (!form) return <p className="text-[#A39E8A]">Se încarcă...</p>;

  const setField = (name, val) => setForm(f => ({ ...f, [name]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site-settings", form);
      toast.success("Site actualizat. Reîmprospătează pagina pentru a vedea modificările.");
      await refresh();
    } catch (e) { toast.error("Eroare la salvare"); }
    setSaving(false);
  };

  // Testimonials
  const tList = form.testimonials || [];
  const setT = (i, k, v) => setForm(f => ({ ...f, testimonials: tList.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const addT = () => setForm(f => ({ ...f, testimonials: [...tList, { name: "", role: "", quote: "" }] }));
  const delT = (i) => setForm(f => ({ ...f, testimonials: tList.filter((_, idx) => idx !== i) }));

  // FAQ
  const fList = form.faq || [];
  const setF = (i, k, v) => setForm(f => ({ ...f, faq: fList.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const addF = () => setForm(f => ({ ...f, faq: [...fList, { q: "", a: "" }] }));
  const delF = (i) => setForm(f => ({ ...f, faq: fList.filter((_, idx) => idx !== i) }));

  return (
    <section data-testid="site-content-editor">
      <div className="flex justify-between items-end mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl text-[#F8F4E6]">Conținut Site</h2>
          <p className="text-sm text-[#A39E8A] mt-1">Editează aici tot textul afișat pe site. După salvare, reîmprospătează pagina (F5) pentru a vedea modificările.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-gold" data-testid="save-settings">{saving ? "Se salvează..." : "Salvează Tot"}</button>
      </div>

      <div className="space-y-3">
        {SECTIONS.map(sec => (
          <details key={sec.key} open={open === sec.key} className="border border-[#D4AF37]/15" data-testid={`sec-${sec.key}`}>
            <summary onClick={(e) => { e.preventDefault(); setOpen(open === sec.key ? "" : sec.key); }} className="cursor-pointer p-5 flex justify-between items-center hover:bg-[#0F0F0F]">
              <span className="font-serif text-xl text-[#F8F4E6]">{sec.title}</span>
              <span className="text-[#D4AF37] text-sm">{open === sec.key ? "−" : "+"}</span>
            </summary>
            <div className="p-6 pt-2 space-y-5">
              {sec.fields.map(f => (
                <div key={f.name}>
                  <label className="label-luxe block mb-2">{f.label}</label>
                  {f.textarea ? (
                    <textarea className={`input-luxe min-h-[80px] ${f.mono ? 'font-mono' : ''}`} value={form[f.name] || ""} onChange={e => setField(f.name, e.target.value)} placeholder={f.placeholder} data-testid={`fld-${f.name}`} />
                  ) : (
                    <input className={`input-luxe ${f.mono ? 'font-mono' : ''}`} value={form[f.name] || ""} onChange={e => setField(f.name, e.target.value)} placeholder={f.placeholder} data-testid={`fld-${f.name}`} />
                  )}
                </div>
              ))}
            </div>
          </details>
        ))}

        {/* Testimonials Section */}
        <details open={open === "testimonials"} className="border border-[#D4AF37]/15" data-testid="sec-testimonials">
          <summary onClick={(e) => { e.preventDefault(); setOpen(open === "testimonials" ? "" : "testimonials"); }} className="cursor-pointer p-5 flex justify-between items-center hover:bg-[#0F0F0F]">
            <span className="font-serif text-xl text-[#F8F4E6]">Testimoniale ({tList.length})</span>
            <span className="text-[#D4AF37] text-sm">{open === "testimonials" ? "−" : "+"}</span>
          </summary>
          <div className="p-6 pt-2 space-y-5">
            {tList.map((t, i) => (
              <div key={i} className="border border-[#D4AF37]/10 p-4 space-y-3" data-testid={`testimonial-${i}`}>
                <div className="flex justify-between items-center">
                  <p className="label-luxe text-[#D4AF37]">Testimonial #{i + 1}</p>
                  <button onClick={() => delT(i)} className="text-[#A39E8A] hover:text-red-400" data-testid={`del-t-${i}`}><Trash2 size={14} /></button>
                </div>
                <input className="input-luxe" placeholder="Nume" value={t.name || ""} onChange={e => setT(i, "name", e.target.value)} />
                <input className="input-luxe" placeholder="Rol (ex. Colecționar, București)" value={t.role || ""} onChange={e => setT(i, "role", e.target.value)} />
                <textarea className="input-luxe" placeholder="Citatul..." value={t.quote || ""} onChange={e => setT(i, "quote", e.target.value)} />
              </div>
            ))}
            <button onClick={addT} className="btn-ghost" data-testid="add-testimonial"><Plus size={14} /> Adaugă Testimonial</button>
          </div>
        </details>

        {/* FAQ Section */}
        <details open={open === "faq"} className="border border-[#D4AF37]/15" data-testid="sec-faq">
          <summary onClick={(e) => { e.preventDefault(); setOpen(open === "faq" ? "" : "faq"); }} className="cursor-pointer p-5 flex justify-between items-center hover:bg-[#0F0F0F]">
            <span className="font-serif text-xl text-[#F8F4E6]">Întrebări Frecvente ({fList.length})</span>
            <span className="text-[#D4AF37] text-sm">{open === "faq" ? "−" : "+"}</span>
          </summary>
          <div className="p-6 pt-2 space-y-5">
            {fList.map((x, i) => (
              <div key={i} className="border border-[#D4AF37]/10 p-4 space-y-3" data-testid={`faq-item-${i}`}>
                <div className="flex justify-between items-center">
                  <p className="label-luxe text-[#D4AF37]">Întrebare #{i + 1}</p>
                  <button onClick={() => delF(i)} className="text-[#A39E8A] hover:text-red-400" data-testid={`del-f-${i}`}><Trash2 size={14} /></button>
                </div>
                <input className="input-luxe" placeholder="Întrebare" value={x.q || ""} onChange={e => setF(i, "q", e.target.value)} />
                <textarea className="input-luxe" placeholder="Răspuns" value={x.a || ""} onChange={e => setF(i, "a", e.target.value)} />
              </div>
            ))}
            <button onClick={addF} className="btn-ghost" data-testid="add-faq"><Plus size={14} /> Adaugă Întrebare</button>
          </div>
        </details>
      </div>

      <div className="mt-8 sticky bottom-4 z-10">
        <button onClick={save} disabled={saving} className="btn-gold w-full justify-center" data-testid="save-settings-bottom">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {saving ? "Se salvează..." : "Salvează Tot"}
        </button>
      </div>
    </section>
  );
}

